import { Box, Switch, Tooltip, styled } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    PaloTotalsBar,
    PaloTotalsEntry,
    PaloTotalsFact,
    usePaloTotalsKeep,
    usePaloTotalsSlot,
} from '@synergy/components-palo';
import { PoolNumberTextfield } from '@synergy/components-next';
import { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { getCurrencySymbol } from '@synergy/utils';

import { FinancialCalculations } from '../../../common/utils/financial-calculations';
import {
    PurchaseOrderDetailPositionsFormValues,
    fieldNames,
} from '../containers/purchase-order-detail-positions/models/purchase-order-positions-form-values';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';

/** Narrow enough for the row, wide enough for a five-digit amount. */
const AmountField = styled(PoolNumberTextfield)(({ theme }) => ({
    // Narrow enough for the row, wide enough for a five-digit amount; the
    // height is the one every other field in the mask has.
    width: theme.spacing(13),

    '& > div': {
        fontSize: '12.5px',

        '& > input': {
            textAlign: 'right',
            fontSize: '12.5px',
        },
    },
}));

export interface PaloPurchaseOrderTotalsBarProps {
    currency: string;
    readonly?: boolean;
}

/**
 * What the order comes to, in one row that stays at the foot of the mask.
 *
 * The same figures used to sit at the end of the position table: on an order
 * with twenty-one positions the table is some 8.000 pixels long, so the amount
 * was three screens below the first position and only reachable by scrolling
 * past everything (Roland Niedermayr, 2026-09-23). Invoice and order now carry
 * the same row.
 */
export const PaloPurchaseOrderTotalsBar = ({ currency, readonly }: PaloPurchaseOrderTotalsBarProps): ReactElement => {
    const { values } = useFormikContext<PurchaseOrderDetailPositionsFormValues>();
    const locale = useAppSelector(bestLocaleSelector);
    const totalsSlot = usePaloTotalsSlot();

    const calculations = FinancialCalculations.calculate(values.allItems, values.netShippingCosts, values.netSurcharge);
    const taxKeys = Object.keys(calculations.taxes)
        .filter(key => '0' !== key)
        .sort()
        .reverse();
    const taxTotal = taxKeys.reduce(
        (sum, key) => sum + calculations.taxes[key as unknown as keyof typeof calculations.taxes].tax,
        0
    );

    const money = (value: number): ReactElement => (
        <FormattedNumber
            value={value}
            currency={currency}
            style="currency"
            minimumFractionDigits={2}
            maximumFractionDigits={2}
        />
    );

    const entries: PaloTotalsEntry[] = [
        { label: <FormattedMessage id="app.label.netPrice" />, value: money(calculations.netTotal) },
        ...(values.isTaxFree || 0 === taxKeys.length
            ? []
            : [
                  {
                      label: (
                          <Tooltip
                              title={taxKeys.map(key => (
                                  <Box key={key}>
                                      {key} %:{' '}
                                      {money(calculations.taxes[key as unknown as keyof typeof calculations.taxes].tax)}
                                  </Box>
                              ))}
                          >
                              <Box component="span">
                                  <FormattedMessage id="app.label.tax" />
                                  {1 === taxKeys.length && ` ${taxKeys[0]} %`}
                              </Box>
                          </Tooltip>
                      ),
                      value: money(taxTotal),
                  },
              ]),
        {
            label: <FormattedMessage id="app.label.invoiceTotal" />,
            value: money(values.isTaxFree ? calculations.netTotal : calculations.grossTotal),
            total: true,
        },
    ];

    const bar = (
        <PaloTotalsBar
            entries={entries}
            facts={
                <>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.positionsNetPrice" />}>
                        {money(calculations.positionTotal)}
                    </PaloTotalsFact>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.deliverCosts" />}>
                        <Field name={fieldNames.netShippingCosts}>
                            {({ field, form }: FieldProps<number>) => (
                                <AmountField
                                    value={field.value}
                                    onChange={event => form.setFieldValue(field.name, Number(event.target.value))}
                                    onBlur={field.onBlur}
                                    disabled={readonly}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    minDecimalScale={2}
                                    endAdornment={getCurrencySymbol(currency, locale)}
                                />
                            )}
                        </Field>
                    </PaloTotalsFact>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.surcharges" />}>
                        <Field name={fieldNames.netSurcharge}>
                            {({ field, form }: FieldProps<number>) => (
                                <AmountField
                                    value={field.value}
                                    onChange={event => form.setFieldValue(field.name, Number(event.target.value))}
                                    onBlur={field.onBlur}
                                    disabled={readonly}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    minDecimalScale={2}
                                    endAdornment={getCurrencySymbol(currency, locale)}
                                />
                            )}
                        </Field>
                    </PaloTotalsFact>
                    <PaloTotalsFact label={<FormattedMessage id="app.label.taxFree" />}>
                        <Field name={fieldNames.isTaxFree}>
                            {({ field }: FieldProps<boolean>) => (
                                <Switch
                                    name={field.name}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    checked={field.value}
                                    color="primary"
                                    size="small"
                                    disabled={readonly}
                                    inputProps={{ 'aria-label': 'Steuerfrei' }}
                                />
                            )}
                        </Field>
                    </PaloTotalsFact>
                </>
            }
        />
    );

    // The figures stay on the tabs this form is not on; what changes them
    // belongs to the form and goes with it (FRM-09).
    usePaloTotalsKeep(
        [calculations.positionTotal, values.netShippingCosts, values.netSurcharge, calculations.netTotal].join('|'),
        () => (
            <PaloTotalsBar
                entries={entries}
                facts={
                    <>
                        <PaloTotalsFact label={<FormattedMessage id="app.label.positionsNetPrice" />}>
                            {money(calculations.positionTotal)}
                        </PaloTotalsFact>
                        <PaloTotalsFact label={<FormattedMessage id="app.label.deliverCosts" />}>
                            {money(values.netShippingCosts ?? 0)}
                        </PaloTotalsFact>
                        <PaloTotalsFact label={<FormattedMessage id="app.label.surcharges" />}>
                            {money(values.netSurcharge ?? 0)}
                        </PaloTotalsFact>
                    </>
                }
            />
        )
    );

    // Into the totals strip of the mask, where it spans the full width and
    // stays put under the tabs — the same place the invoice keeps its totals.
    return totalsSlot ? createPortal(bar, totalsSlot) : bar;
};
